import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Theme, Typography, useMediaQuery } from "@mui/material";
import AddIcon from "@icons/myicons/plus.svg?react";
import { useAccountsPerformance } from "@components/Accounts/AccountView/hooks/useAccountsPerformance";
import { FilterValue } from "@components/filters";
import { AddPortfolio, AddPortfolioHandle } from "@features/add/AddPortfolio";
import { ImportType } from "@features/add/components/types";
import {
  BottomControlsPanel,
  CardPageLayout,
  LoadingPlaceHolder,
  NoDataStub,
} from "@rentablo/components";
import { AdaptiveButton } from "@components";
import { AccountsListFilters } from "./AccountsListFilters";
import { AccountsListSettings } from "./AccountsListSettings";
import { AccountsListGrid } from "./AccountsListGrid";
import { ActionButton } from "./ActionButton";
import { useAccountsByGroup } from "./useAccountsByGroup";

export const DepotsAccountsPage = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const addPortfolioRef = useRef<AddPortfolioHandle | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [selectedChips, setSelectedChips] = useState<FilterValue[]>(["all"]);
  const [filter, setFilter] = useState("");

  const {
    accounts,
    groupedAccounts,
    refMapByDepotId,
    isLoading: isAccountsLoading,
    hiddenAccountsCount,
    groups,
  } = useAccountsByGroup({ showHidden });
  const groupsToShow = selectedChips.includes("all") ? groups : selectedChips;

  const performanceById = useAccountsPerformance(accounts);
  const performanceValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(performanceById).map(([id, p]) => [id, p.portfolioValue])
      ),
    [performanceById]
  );

  const onSelect = useCallback((val: ImportType) => {
    addPortfolioRef.current?.onSelect(val);
  }, []);

  const addButton = (
    <AddPortfolio
      ref={addPortfolioRef}
      directOpenType={{ default: "01_depot", type: "" }}
      mobileTitle={t("accounts.addButton")}
      anchor={
        isMobile ? (
          <AdaptiveButton
            iconOnly
            icon={<AddIcon width="16px" height="16px" />}
            label={t("accounts.addButton")}
            testId="add-account"
          />
        ) : (
          <Button
            variant="primary"
            startIcon={<AddIcon width="16px" height="16px" />}
            data-testid="add-account"
            sx={{
              padding: "10px 16px 10px 20px",
              minWidth: "144px",
            }}
          >
            {t("accounts.addButton")}
          </Button>
        )
      }
    />
  );

  useEffect(() => {
    if (!isAccountsLoading && accounts?.length === 0)
      addPortfolioRef.current?.openModal();
  }, [isAccountsLoading, accounts?.length]);

  return (
    <CardPageLayout
      hasBottomControlsPanel={isMobile}
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        { text: t("breadcrumbs.depotsAccounts") },
      ]}
      cardHeading={
        isMobile ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
          >
            <Typography variant="h4">{t("depots.title")}</Typography>
            {!isAccountsLoading && accounts?.length ? (
              <AccountsListSettings
                groups={groups}
                selectedChips={selectedChips}
                setSelectedChips={setSelectedChips}
                hiddenAccountsCount={hiddenAccountsCount}
                showHidden={showHidden}
                setShowHidden={setShowHidden}
                filter={filter}
                setFilter={setFilter}
              />
            ) : null}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={5}
          >
            <Typography variant="h4">{t("depots.title")}</Typography>
            <ActionButton
              filter={filter}
              setFilter={setFilter}
              addButton={addButton}
            />
          </Box>
        )
      }
      cardChildren={
        <>
          {isAccountsLoading ? (
            <Box width="100%" mt={6}>
              <LoadingPlaceHolder height="300px" />
            </Box>
          ) : Object.keys(groupedAccounts)?.length === 0 ? (
            <Box mt={10} mb={6}>
              <NoDataStub />
            </Box>
          ) : (
            <>
              <AccountsListFilters
                groups={groups}
                selectedChips={selectedChips}
                setSelectedChips={setSelectedChips}
                hiddenAccountsCount={hiddenAccountsCount}
                showHidden={showHidden}
                setShowHidden={setShowHidden}
                showControls={!isMobile}
              />
              <AccountsListGrid
                groupedAccounts={groupedAccounts}
                groupsToShow={groupsToShow}
                refMapByDepotId={refMapByDepotId}
                isAccountsLoading={isAccountsLoading}
                filter={filter}
                onSelect={onSelect}
                performanceById={performanceById}
                performanceValues={performanceValues}
              />
            </>
          )}
        </>
      }
    >
      {isMobile && (
        <BottomControlsPanel actions={addButton}>{null}</BottomControlsPanel>
      )}
    </CardPageLayout>
  );
};
